/**
 * Viewer Capabilities Component
 * Shown to self-service signup users with 'viewer' role
 * Displays platform capabilities organized by ICP type (Employers, Health Plans, Providers)
 * Opens Calendly in new tab for reliable booking across all browsers
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  CheckCircle,
  Building2,
  Heart,
  Stethoscope,
  Users,
  DollarSign,
  TrendingUp,
  Shield,
  Clock,
  FileText,
  Target,
  Briefcase,
  Activity,
  Network,
  BarChart3,
  HeartHandshake,
  ArrowRight,
  Sparkles,
  Copy,
  Check,
  KeyRound,
  Loader2,
  X
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { userManagementAPI, type EnableSandboxResponse } from '@/services/api/userManagement';

interface ViewerCapabilitiesProps {
  user?: {
    name?: string;
    email?: string;
    role?: string;
  } | null;
}

// Default Calendly URL - will be overridden by platform settings if available
const DEFAULT_CALENDLY_URL = 'https://calendly.com/weavein-demo/30min';

// ICP type definitions
type ICPType = 'employer' | 'healthplan' | 'provider';

interface ICPCapability {
  icon: React.ElementType;
  title: string;
  description: string;
  features: string[];
}

interface ICPSection {
  id: ICPType;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  gradient: string;
  iconBg: string;
  capabilities: ICPCapability[];
}

export function ViewerCapabilities({ user }: ViewerCapabilitiesProps) {
  const [calendlyUrl, setCalendlyUrl] = useState(DEFAULT_CALENDLY_URL);
  const [activeICP, setActiveICP] = useState<ICPType>('employer');

  // Self-serve sandbox upgrade (viewer → 30-day sandbox with API access)
  const [enabling, setEnabling] = useState(false);
  const [enableError, setEnableError] = useState<string | null>(null);
  const [sandboxResult, setSandboxResult] = useState<EnableSandboxResponse | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  const handleEnableSandbox = useCallback(async () => {
    setEnableError(null);
    setEnabling(true);
    try {
      const res = await userManagementAPI.enableSandbox();
      // Adopt the fresh org_admin token so the caller has elevated access.
      if (res.auth_token) {
        localStorage.setItem('auth_token', res.auth_token);
        localStorage.setItem('active_cbo_id', String(res.organization_id));
      }
      // If an API key came back, surface it once (it is never shown again).
      // Otherwise drop the user straight into their new sandbox dashboard.
      if (res.api_credentials?.api_key) {
        setSandboxResult(res);
      } else {
        window.location.href = '/';
      }
    } catch (err: any) {
      setEnableError(
        err?.response?.data?.detail || 'Could not enable your sandbox. Please try again or contact support.'
      );
    } finally {
      setEnabling(false);
    }
  }, []);

  const copyApiKey = useCallback(async () => {
    const key = sandboxResult?.api_credentials?.api_key;
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch {
      /* clipboard unavailable — user can select manually */
    }
  }, [sandboxResult]);

  // Load Calendly URL from platform settings
  useEffect(() => {
    const loadCalendlyUrl = async () => {
      try {
        const response = await apiClient.get('/api/platform-settings/calendly-url');
        if (response.data?.url) {
          setCalendlyUrl(response.data.url);
        }
      } catch (error) {
        // Use default URL if not configured or API fails
        console.log('Using default Calendly URL');
      }
    };
    loadCalendlyUrl();
  }, []);

  // Build Calendly URL with prefill parameters
  // Note: For Calendly routing URLs (/d/ format), we use simple URL without prefill
  // to avoid compatibility issues. The user can enter their details on Calendly.
  const getCalendlyUrl = useCallback(() => {
    // Check if it's a routing URL (contains /d/) - these have issues with prefill params
    if (calendlyUrl.includes('/d/')) {
      return calendlyUrl;
    }

    // For standard Calendly URLs, add prefill parameters
    const params = new URLSearchParams();
    if (user?.name) params.set('name', user.name);
    if (user?.email) params.set('email', user.email);

    return params.toString()
      ? `${calendlyUrl}?${params.toString()}`
      : calendlyUrl;
  }, [calendlyUrl, user]);

  // ICP-based capability sections
  const icpSections: ICPSection[] = [
    {
      id: 'employer',
      title: 'For Employers',
      subtitle: 'Support your workforce with comprehensive benefits navigation and care coordination',
      icon: Building2,
      gradient: 'from-blue-500 to-indigo-600',
      iconBg: 'from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/40',
      capabilities: [
        {
          icon: Users,
          title: 'Employee Benefits Navigation',
          description: 'Help employees find and access the right benefits, reducing confusion and maximizing utilization.',
          features: ['Personalized benefits guidance', 'Care navigation support', 'Resource matching', 'Dependent coverage assistance'],
        },
        {
          icon: DollarSign,
          title: 'Healthcare Cost Management',
          description: 'Reduce healthcare costs through proactive care management and early intervention programs.',
          features: ['Cost analytics dashboard', 'High-cost claimant support', 'Preventive care tracking', 'ROI reporting'],
        },
        {
          icon: TrendingUp,
          title: 'Workforce Wellness Programs',
          description: 'Create and manage wellness initiatives that improve employee health and reduce absenteeism.',
          features: ['Program enrollment tracking', 'Engagement metrics', 'Outcome measurement', 'Incentive management'],
        },
        {
          icon: Shield,
          title: 'Compliance & Reporting',
          description: 'Stay compliant with healthcare regulations and generate required reports effortlessly.',
          features: ['HIPAA compliance', 'ACA reporting', 'Audit trails', 'Data security'],
        },
      ],
    },
    {
      id: 'healthplan',
      title: 'For Health Plans',
      subtitle: 'Enhance member engagement and improve health outcomes at scale',
      icon: Heart,
      gradient: 'from-rose-500 to-pink-600',
      iconBg: 'from-rose-100 to-pink-100 dark:from-rose-900/40 dark:to-pink-900/40',
      capabilities: [
        {
          icon: Target,
          title: 'Population Health Management',
          description: 'Identify and stratify member populations for targeted interventions and improved outcomes.',
          features: ['Risk stratification', 'Gap-in-care identification', 'Predictive analytics', 'Cohort management'],
        },
        {
          icon: Network,
          title: 'Provider Network Coordination',
          description: 'Connect members with in-network providers and community resources seamlessly.',
          features: ['Provider directory integration', 'Referral management', 'Network adequacy tracking', 'CBO partnerships'],
        },
        {
          icon: Activity,
          title: 'Care Management Programs',
          description: 'Deploy evidence-based care management programs that reduce costs and improve HEDIS scores.',
          features: ['Chronic disease management', 'Transitional care', 'Medication adherence', 'Preventive screenings'],
        },
        {
          icon: BarChart3,
          title: 'Quality & Performance',
          description: 'Track quality metrics, star ratings, and performance indicators in real-time.',
          features: ['HEDIS measure tracking', 'Star rating analytics', 'Member satisfaction', 'Performance dashboards'],
        },
      ],
    },
    {
      id: 'provider',
      title: 'For Providers & CBOs',
      subtitle: 'Streamline care coordination and demonstrate measurable community impact',
      icon: Stethoscope,
      gradient: 'from-teal-500 to-emerald-600',
      iconBg: 'from-teal-100 to-emerald-100 dark:from-teal-900/40 dark:to-emerald-900/40',
      capabilities: [
        {
          icon: HeartHandshake,
          title: 'Social Determinants of Health',
          description: 'Screen for and address SDOH needs with integrated community resource referrals.',
          features: ['SDOH screening tools', 'Resource directory', 'Closed-loop referrals', 'Need tracking'],
        },
        {
          icon: Clock,
          title: 'Care Coordination Workflow',
          description: 'Manage patient care plans, track progress, and coordinate across care teams efficiently.',
          features: ['Care plan management', 'Task assignment', 'Progress tracking', 'Team collaboration'],
        },
        {
          icon: FileText,
          title: 'Documentation & Billing',
          description: 'Streamline documentation for community health worker services and maximize reimbursement.',
          features: ['CHW documentation', 'Service tracking', 'Billing integration', 'Outcome reporting'],
        },
        {
          icon: Briefcase,
          title: 'Grant & Contract Management',
          description: 'Track program deliverables, demonstrate outcomes, and manage multiple funding sources.',
          features: ['Deliverable tracking', 'Outcome measurement', 'Funder reporting', 'Multi-program support'],
        },
      ],
    },
  ];

  const activeSection = icpSections.find(s => s.id === activeICP)!;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {/* Sandbox-ready modal: shown once after self-serve provisioning so the
          user can copy their API key (it is never displayed again). */}
      {sandboxResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 relative">
            <button
              type="button"
              onClick={() => (window.location.href = '/')}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Your sandbox is ready</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Full platform access with sample data — expires in 30 days.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 mb-4">
              <div className="flex items-center gap-2 mb-2 text-sm font-medium text-amber-800 dark:text-amber-300">
                <KeyRound className="w-4 h-4" />
                Sandbox API key
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
                Copy this now — for security it will not be shown again.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded-lg bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs font-mono text-gray-800 dark:text-gray-200">
                  {sandboxResult.api_credentials?.api_key}
                </code>
                <button
                  type="button"
                  onClick={copyApiKey}
                  className="inline-flex items-center px-3 py-2 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700"
                >
                  {copiedKey ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="ml-1.5">{copiedKey ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
              {sandboxResult.api_credentials?.client_id && (
                <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">
                  Client ID: <span className="font-mono">{sandboxResult.api_credentials.client_id}</span>
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => (window.location.href = '/')}
              className="w-full inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-teal-600 to-blue-600 text-white font-semibold rounded-xl hover:from-teal-700 hover:to-blue-700"
            >
              Enter your sandbox
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Trust Marketplace</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Care Coordination Platform</p>
            </div>
            {user && (
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 mt-1">
                  Viewer Account
                </span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Connect Care. Coordinate Better.
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            Trust Marketplace brings together employers, health plans, and community providers
            to deliver whole-person care that improves outcomes and reduces costs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={handleEnableSandbox}
              disabled={enabling}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-700 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
            >
              {enabling ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Setting up your sandbox…
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Try a Free Sandbox
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
            <a
              href={getCalendlyUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-white dark:bg-gray-800 text-teal-700 dark:text-teal-300 font-semibold rounded-xl border border-teal-200 dark:border-teal-800 hover:bg-teal-50 dark:hover:bg-gray-700 shadow-sm transition-all"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule a Demo
            </a>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            The sandbox unlocks full platform access with sample data, forms, workflows, and API
            credentials — free for 30 days, no demo required.
          </p>
          {enableError && (
            <div className="mt-4 inline-flex items-center px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {enableError}
            </div>
          )}
        </div>

        {/* ICP Type Selector */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white dark:bg-gray-800 rounded-2xl p-2 shadow-lg border border-gray-200 dark:border-gray-700">
            {icpSections.map((section) => {
              const IconComponent = section.icon;
              const isActive = activeICP === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveICP(section.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    isActive
                      ? `bg-gradient-to-r ${section.gradient} text-white shadow-md`
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <IconComponent className="w-5 h-5" />
                  <span className="hidden sm:inline">{section.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active ICP Section Header */}
        <div className="text-center mb-10">
          <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${activeSection.gradient} rounded-2xl mb-4 shadow-lg`}>
            <activeSection.icon className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {activeSection.title}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {activeSection.subtitle}
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {activeSection.capabilities.map((cap, index) => {
            const IconComponent = cap.icon;
            return (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${activeSection.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                  <IconComponent className={`w-7 h-7 ${
                    activeICP === 'employer' ? 'text-blue-600 dark:text-blue-400' :
                    activeICP === 'healthplan' ? 'text-rose-600 dark:text-rose-400' :
                    'text-teal-600 dark:text-teal-400'
                  }`} />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{cap.title}</h4>
                <p className="text-gray-600 dark:text-gray-300 mb-4">{cap.description}</p>
                <ul className="space-y-2">
                  {cap.features.map((feature, idx) => (
                    <li key={idx} className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                      <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 ${
                        activeICP === 'employer' ? 'text-blue-500' :
                        activeICP === 'healthplan' ? 'text-rose-500' :
                        'text-teal-500'
                      }`} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Value Props Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-blue-100 dark:border-blue-800">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">40%</div>
            <div className="text-gray-900 dark:text-white font-medium mb-1">Reduction in Admin Time</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Streamlined workflows and automated coordination</p>
          </div>
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 rounded-2xl p-6 border border-rose-100 dark:border-rose-800">
            <div className="text-3xl font-bold text-rose-600 dark:text-rose-400 mb-2">2x</div>
            <div className="text-gray-900 dark:text-white font-medium mb-1">Member Engagement</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Better outreach and follow-up capabilities</p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border border-teal-100 dark:border-teal-800">
            <div className="text-3xl font-bold text-teal-600 dark:text-teal-400 mb-2">95%</div>
            <div className="text-gray-900 dark:text-white font-medium mb-1">Referral Closure Rate</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Closed-loop tracking ensures follow-through</p>
          </div>
        </div>

        {/* CTA Banner */}
        <div className={`bg-gradient-to-r ${activeSection.gradient} rounded-2xl p-8 text-center text-white shadow-xl`}>
          <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Care Coordination?</h3>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            Schedule a personalized demo to see how Trust Marketplace can help your organization
            connect care, improve outcomes, and reduce costs.
          </p>
          <a
            href={getCalendlyUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-100 shadow-lg transition-all transform hover:-translate-y-0.5"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Schedule Demo Now
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>
    </div>
  );
}

export default ViewerCapabilities;
